// Service layer for org-managed MCP tools. Targets the
// agentic-backend v1 routes (`target: 'workflow'`), all gated by
// require_org_owner. See src/api/v1/routers/org_mcp_tools.py.
import { apiRequest } from '@/utils/api-request';
import {
  CreateResult,
  OrgMcpTool,
  OrgMcpToolCreateInput,
  OrgMcpToolError,
  OrgMcpToolUpdateInput,
  ValidateResult,
} from './types';

const base = (orgId: string) => `/api/v1/org-mcp-tools/${orgId}`;

// The backend returns typed validation failures as HTTP 400 with
// `detail: {code, message}`. api-request stringifies non-string `detail` to a
// generic message but preserves the raw body in `data`, so dig the object out.
const extractError = (response: { detail?: string; data?: unknown }): Error => {
  const raw = (response.data as { detail?: unknown } | undefined)?.detail;
  if (raw && typeof raw === 'object' && 'message' in raw) {
    const typed = raw as OrgMcpToolError;
    const err = new Error(typed.message) as Error & { code?: string };
    err.code = typed.code;
    return err;
  }
  return new Error(response.detail || 'Request failed');
};

export const listOrgMcpTools = async (orgId: string): Promise<OrgMcpTool[]> => {
  const response = await apiRequest<{ tools: OrgMcpTool[] }>({
    url: base(orgId),
    method: 'GET',
    target: 'workflow',
  });
  if (response?.detail) throw extractError(response);
  return response?.data?.tools ?? [];
};

export const validateOrgMcpTool = async (
  orgId: string,
  input: OrgMcpToolCreateInput,
): Promise<ValidateResult> => {
  const response = await apiRequest<ValidateResult>({
    url: `${base(orgId)}/validate`,
    method: 'POST',
    target: 'workflow',
    body: input as unknown as Record<string, unknown>,
    // 400 here is an expected validation outcome, not an incident.
    suppressSentryStatuses: [400],
  });
  if (response?.detail) throw extractError(response);
  return response?.data ?? { ok: false, tools: [] };
};

// Re-discover an existing tool's tools using its STORED secret (SSM) — no
// client-supplied credential. Refreshes the persisted available_tools so the
// per-tool toggle list reflects the server's current tools.
export const refreshOrgMcpToolTools = async (
  orgId: string,
  toolId: string,
): Promise<ValidateResult> => {
  const response = await apiRequest<ValidateResult>({
    url: `${base(orgId)}/${toolId}/refresh-tools`,
    method: 'POST',
    target: 'workflow',
    suppressSentryStatuses: [400],
  });
  if (response?.detail) throw extractError(response);
  return response?.data ?? { ok: false, tools: [] };
};

export const createOrgMcpTool = async (
  orgId: string,
  input: OrgMcpToolCreateInput,
): Promise<CreateResult> => {
  const response = await apiRequest<CreateResult>({
    url: base(orgId),
    method: 'POST',
    target: 'workflow',
    body: input as unknown as Record<string, unknown>,
    suppressSentryStatuses: [400],
  });
  if (response?.detail) throw extractError(response);
  if (!response?.data) throw new Error('No response from server');
  return response.data;
};

export const updateOrgMcpTool = async (
  orgId: string,
  toolId: string,
  input: OrgMcpToolUpdateInput,
): Promise<OrgMcpTool> => {
  const response = await apiRequest<OrgMcpTool>({
    url: `${base(orgId)}/${toolId}`,
    method: 'PUT',
    target: 'workflow',
    body: input as unknown as Record<string, unknown>,
    suppressSentryStatuses: [400],
  });
  if (response?.detail) throw extractError(response);
  if (!response?.data) throw new Error('No response from server');
  return response.data;
};

export const toggleOrgMcpTool = async (
  orgId: string,
  toolId: string,
  enabled: boolean,
): Promise<OrgMcpTool> => {
  const response = await apiRequest<OrgMcpTool>({
    url: `${base(orgId)}/${toolId}`,
    method: 'PATCH',
    target: 'workflow',
    body: { enabled },
  });
  if (response?.detail) throw extractError(response);
  if (!response?.data) throw new Error('No response from server');
  return response.data;
};

export const deleteOrgMcpTool = async (
  orgId: string,
  toolId: string,
): Promise<void> => {
  const response = await apiRequest<unknown>({
    url: `${base(orgId)}/${toolId}`,
    method: 'DELETE',
    target: 'workflow',
  });
  if (response?.detail) throw extractError(response);
};
